import { useCallback, useEffect, useState } from 'react'
import type { USSchool } from '@mote/api'
import { useUSSchoolsMutation } from '@mote/api'
import {
  MOTE_DEBUG_US_SCHOOLS_LABS_MODE_STORAGE_KEY,
  MOTE_DEBUG_US_SCHOOLS_STORAGE_KEY
} from '@mote/helpers/constants'
import { reallyTruthy } from '@mote/helpers/validation'
import { Block, Button, Text } from '@mote/ui'
import { x } from '@xstyled/styled-components'
import { useOnboarding } from '../context'
import type { Choice, QuestionValues } from '../typings'
import { AsyncSelect } from './AsyncSelect'
import { DebugUSSchoolOptions } from './DebugUSSchoolOptions'
import { Hint } from './Hint'
import { InputAnswer } from './InputAnswer'
import { SchoolHints } from './SchoolHints'

export interface SchoolAnswerProps extends Pick<Choice, 'text'> {
  surveyKey: QuestionValues['key']
  question: QuestionValues['question']
}

export const SchoolAnswer = (props: SchoolAnswerProps) => {
  const onboarding = useOnboarding()
  const [enterUSSchoolManually, setEnterUSSchoolManually] = useState(false)

  const isUS = () =>
    reallyTruthy(localStorage.getItem(MOTE_DEBUG_US_SCHOOLS_STORAGE_KEY)) ||
    onboarding.canSchoolSearchDuringUserOnboarding

  useEffect(() => {
    if (!isUS()) return

    onboarding.updateState({
      schoolNameEntryType: enterUSSchoolManually ? 'manual' : 'search'
    })
  }, [enterUSSchoolManually])

  if (isUS()) {
    return (
      <Block gap={16}>
        <USCountry disabled={enterUSSchoolManually} {...props} />
        <Block gap={16}>
          <Text text='md'>
            {"Can't find your school? "}
            <Button
              // @ts-expect-error
              variant='link'
              text={enterUSSchoolManually ? 'Cancel' : 'Enter manually'}
              onClick={() => setEnterUSSchoolManually(prev => !prev)}
            />
          </Text>

          {enterUSSchoolManually && (
            <InputAnswer
              surveyKey={props.surveyKey}
              question={props.question}
              placeholder='Mote High School'
              text={props.text}
            />
          )}
        </Block>
      </Block>
    )
  }

  return <NonUSCountry {...props} />
}

const NonUSCountry = (props: SchoolAnswerProps): JSX.Element => {
  const onboarding = useOnboarding()
  const [selectedHint, setSelectedHint] = useState<string>(
    (onboarding.state.survey[props.surveyKey]?.value as string) ?? ''
  )
  const hasHints =
    Array.isArray(onboarding.institutionNameHints) &&
    onboarding.institutionNameHints.length > 0
  const hasHint =
    hasHints &&
    Boolean(
      onboarding.institutionNameHints.find(
        hint => hint === onboarding.state.survey[props.surveyKey]?.value
      )
    )

  function handleHintSelection(hint: string) {
    const survey = onboarding.state.survey
    survey[props.surveyKey] = {
      question: props.question,
      label: props.text,
      value: hint
    }

    onboarding.updateState({ survey, schoolNameEntryType: 'hint' })
    setSelectedHint(hint)
  }

  function handleHintClear() {
    const survey = onboarding.state.survey
    survey[props.surveyKey] = {
      question: props.question,
      label: props.text,
      value: ''
    }

    onboarding.updateState({ survey, schoolNameEntryType: 'manual' })
    setSelectedHint('')
  }

  useEffect(() => {
    if (hasHint) {
      setSelectedHint(
        s => (onboarding.state.survey[props.surveyKey]?.value as string) ?? s
      )
    }
  }, [hasHint])

  return (
    <Block layout='vertical' justify='center' gap={16} spreadX>
      {hasHint && (
        <x.div
          display='flex'
          alignItems='center'
          px={4}
          h={12}
          w='full'
          borderRadius='md'
          border='1px solid'
          borderColor='grey.l2'
        >
          <Hint text={selectedHint} onClear={handleHintClear} isClearable />
        </x.div>
      )}
      {!hasHint && (
        <InputAnswer
          surveyKey={props.surveyKey}
          question={props.question}
          placeholder='Mote High School'
          text={props.text}
        />
      )}
      {hasHints && (
        <SchoolHints
          selectedHint={selectedHint}
          onHintSelection={handleHintSelection}
        />
      )}
    </Block>
  )
}

export interface USCountryProps extends SchoolAnswerProps {
  disabled: boolean
}

const USCountry = (props: USCountryProps) => {
  const { state, updateState } = useOnboarding()
  const [selectedValue, setSelectedValue] = useState<USSchool | null>(null)
  const [defaultOptions, setDefaultOptions] = useState<
    Array<USSchool> | undefined
  >()
  const [networkBusy, setNetworkBusy] = useState<boolean>(false)
  const [networkError, setNetworkError] = useState<boolean | string>(false)
  const [stateOverride, setStateOverride] = useState<string>('')
  const [disableGeoIp, setDisableGeoIp] = useState<boolean>(false)
  const usSchoolsMutation = useUSSchoolsMutation()
  const isUSDebugMode = reallyTruthy(
    window.localStorage.getItem(MOTE_DEBUG_US_SCHOOLS_LABS_MODE_STORAGE_KEY)
  )

  const loadOptions = useCallback(
    (text: string) => {
      if (!text.length) {
        reset()
        return
      }

      setNetworkBusy(true)

      return usSchoolsMutation
        .mutate({
          variables: {
            searchTerm: text,
            stateOverride,
            disableGeoIp
          }
        })
        .then(({ data: response }) => {
          if (!response) return

          const data = response.search?.matches

          setNetworkBusy(false)
          setDefaultOptions(data)

          return data as Array<USSchool>
        })
        .catch(e => {
          console.error(e)
          setNetworkBusy(false)
          setNetworkError('Something bad happened')
          return [] as Array<USSchool>
        }) as Promise<Array<USSchool>>
    },
    [stateOverride, disableGeoIp]
  )

  function reset() {
    const survey = state.survey

    survey[props.surveyKey] = {
      question: props.question,
      label: props.text,
      value: ''
    }
    updateState({ survey })

    setNetworkError(false)
    setNetworkBusy(false)
    setDefaultOptions(undefined)
    setSelectedValue(null)
  }

  function handleChange(data: USSchool | null) {
    if (!data) {
      reset()
      return
    }

    const { forSubmission } = data
    const survey = state.survey
    survey[props.surveyKey] = {
      question: props.question,
      label: props.text,
      value: forSubmission
    }

    setSelectedValue(data)
    updateState({ survey })
  }

  useEffect(() => {
    reset()
  }, [props.disabled])

  return (
    <>
      <AsyncSelect
        isDisabled={props.disabled}
        cacheOptions
        isClearable
        defaultOptions={defaultOptions}
        placeholder='Search by ZIP or School name'
        noOptionsMessage={() => 'No results...'}
        value={selectedValue}
        getOptionLabel={option => {
          if (!option) return ''

          const {
            forDisplay: { schoolName }
          } = option

          return schoolName
        }}
        getOptionValue={option => option.forDisplay.schoolName}
        loadOptions={loadOptions}
        onChange={handleChange}
      />
      {isUSDebugMode && (
        <DebugUSSchoolOptions
          debugState={{
            stateOverride,
            networkBusy,
            disableGeoIp,
            networkError
          }}
          updateState={{ setDisableGeoIp, setStateOverride }}
        />
      )}
    </>
  )
}
